var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Mesh as BABYLON_Mesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.js';
import { TransformNode as BABYLON_TransformNode } from '@inter-ikea-betrakta-babylonjs/core/Meshes/transformNode.js';
import { IVector3, IMatrix4, IBoundingBox, ITransformedBoundingBox } from '../api.js';
import { ViewerUiMeshMaterial, ALBEDO_ALPHA_INSTANCE_ATTRIBUTE_NAME } from './material/ViewerUiMeshMaterial.js';
import { Vector4 as BABYLON_Vector4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Vector3ConstantReference } from './material/PropertyWrappers.js';
import { Disposable } from '../utils/Disposable.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { determinant3x3 } from '../Math.js';
import { Observable } from '../Observable.js';
import { RENDER_LAYER_UI, RenderLayerMask } from './RenderLayer.js';
import { Deprecated } from '../utils/Deprecated.js';
import { babylon_change_source_mesh } from './scene/SceneAssetMesh.js';
import { ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME } from './material/ViewerMaterial.js';
import { OutlineLayer } from './OutlineEffect.js';
import { Logger } from '../logging/Logger.js';
const LOGGER_CATEGORY = 'UiMesh';
let UiMesh = class UiMesh {
    constructor(viewer) {
        this.viewer = viewer;
        this._instances = new Set();
        this._root = null;
        this._meshes = null;
        this._material = new ViewerUiMeshMaterial(this.viewer);
        this.draw_on_top = false;
        this._local_bounds = IBoundingBox.Invalid();
    }
    get draw_on_top() {
        return this._material.depthFunction === BABYLON_Constants.ALWAYS;
    }
    set draw_on_top(value) {
        const wanted_depth_function = value ? BABYLON_Constants.ALWAYS : BABYLON_Constants.LEQUAL;
        if (this._material.depthFunction !== wanted_depth_function) {
            this._material.depthFunction = wanted_depth_function;
            this.viewer.mark_scene_dirty();
        }
    }
    load_mesh(description) {
        var _a, _b;
        if (this._root !== null) {
            this._instances.forEach((instance) => {
                instance.dispose_meshes();
            });
            (_a = this._meshes) === null || _a === void 0 ? void 0 : _a.forEach((pair) => {
                pair.positive.dispose();
                pair.negative.dispose();
            });
            this._root.dispose();
            this._root = null;
            this._meshes = null;
        }
        this._root = new BABYLON_TransformNode('SourceMesh UiMesh :)');
        const meshes = this.viewer.mesh_manager.create_custom_mesh_babylon({
            positions: description.positions,
            indices: description.indices,
            colors: (_b = description.colors) !== null && _b !== void 0 ? _b : null,
            normals: null,
            tangents: null,
            uv0: null,
            uv1: null
        });
        meshes.forEach((mesh) => { mesh.parent = this._root; });
        this._local_bounds.min = new IVector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
        this._local_bounds.max = new IVector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
        for (let i = 0; i < description.positions.length; i += 3) {
            const x = description.positions[i + 0];
            const y = description.positions[i + 1];
            const z = description.positions[i + 2];
            this._local_bounds.min.x = Math.min(this._local_bounds.min.x, x);
            this._local_bounds.min.y = Math.min(this._local_bounds.min.y, y);
            this._local_bounds.min.z = Math.min(this._local_bounds.min.z, z);
            this._local_bounds.max.x = Math.max(this._local_bounds.max.x, x);
            this._local_bounds.max.y = Math.max(this._local_bounds.max.y, y);
            this._local_bounds.max.z = Math.max(this._local_bounds.max.z, z);
        }
        this._meshes = meshes.map((mesh) => {
            if (mesh instanceof BABYLON_Mesh) {
                mesh.registerInstancedBuffer(OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME, OutlineLayer.INSTANCE_DATA_ATTRIBUTE_STRIDE);
                mesh.registerInstancedBuffer(ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME, 4);
                mesh.instancedBuffers[OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME] = new BABYLON_Vector4(0, 0, 0, 0);
                mesh.instancedBuffers[ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME] = new BABYLON_Vector4(1, 1, 1, 1);
            }
            else {
                throw new Error('Invalid Babylon mesh type for gizmo mesh');
            }
            mesh.registerInstancedBuffer(ALBEDO_ALPHA_INSTANCE_ATTRIBUTE_NAME, 4);
            mesh.instancedBuffers[ALBEDO_ALPHA_INSTANCE_ATTRIBUTE_NAME] = BABYLON_Vector4.One();
            mesh.material = this._material;
            const negative_mesh = mesh.clone('Negative gizmo source mesh', null, false);
            negative_mesh.scaling.x *= -1.0; // Invert determinant by negating scaling on one axis
            negative_mesh.registerInstancedBuffer(ALBEDO_ALPHA_INSTANCE_ATTRIBUTE_NAME, 4);
            negative_mesh.instancedBuffers[ALBEDO_ALPHA_INSTANCE_ATTRIBUTE_NAME] = BABYLON_Vector4.One();
            return { positive: mesh, negative: negative_mesh };
        });
        this._instances.forEach((instance) => {
            this.load_instance(instance);
        });
        // Disable the mesh so Babylon doesn't see it as an active participant in the scene
        this._root.setEnabled(false);
    }
    dispose() {
        var _a;
        if (this._root !== null) {
            this._instances.forEach((instance) => {
                instance.dispose_meshes();
            });
            (_a = this._meshes) === null || _a === void 0 ? void 0 : _a.forEach((pair) => {
                pair.positive.dispose();
                pair.negative.dispose();
                this._meshes = null;
            });
            this._root.dispose();
            this._root = null;
        }
        this._material.dispose();
    }
    create_instance() {
        const instance = new UiMeshInstance(this);
        this._instances.add(instance);
        this.load_instance(instance);
        return instance;
    }
    dispose_instances() {
        for (const instance of this._instances) {
            instance.dispose();
        }
    }
    create_gizmo() {
        const instance = new Gizmo(this);
        this._instances.add(instance);
        this.load_instance(instance);
        return instance;
    }
    load_instance(instance) {
        var _a;
        instance.dispose_meshes();
        (_a = this._meshes) === null || _a === void 0 ? void 0 : _a.forEach((pair) => {
            if (pair.positive instanceof BABYLON_Mesh) {
                const instanced_mesh = pair.positive.createInstance('gizmo instance');
                instanced_mesh.instancedBuffers[ALBEDO_ALPHA_INSTANCE_ATTRIBUTE_NAME] = instance.color_alpha_factor;
                instance.instances.push(instanced_mesh);
                instanced_mesh.parent = instance.root;
                if (!instanced_mesh.metadata) {
                    instanced_mesh.metadata = {};
                }
                instanced_mesh.metadata['gizmo_source_meshes'] = pair;
            }
        });
        instance.update_babylon_mesh_settings();
        this.viewer.mark_scene_dirty();
    }
    disconnect_instance(instance) {
        this._instances.delete(instance);
    }
    get local_bounds() {
        return this._local_bounds;
    }
};
UiMesh = __decorate([
    Disposable
], UiMesh);
export { UiMesh };
export class UiMeshInstance {
    constructor(mesh) {
        this._parent = null;
        this._render_layer_mask = new RenderLayerMask([RENDER_LAYER_UI]);
        this._disposed_observable = new Observable();
        this.instances = [];
        this.mesh_source = mesh;
        this.root = new BABYLON_TransformNode('gizmo instance');
        this.color_alpha_factor = new BABYLON_Vector4(1, 1, 1, 1);
        this._position = new Vector3ConstantReference(this.root, 'position', this.mesh_source.viewer.mark_scene_dirty.bind(this.mesh_source.viewer));
        this._rotation = new Vector3ConstantReference(this.root, 'rotation', this.mesh_source.viewer.mark_scene_dirty.bind(this.mesh_source.viewer));
        this._scale = new Vector3ConstantReference(this.root, 'scaling', this.mesh_source.viewer.mark_scene_dirty.bind(this.mesh_source.viewer));
        this._color = new Vector3ConstantReference(this, 'color_alpha_factor', this.mesh_source.viewer.mark_scene_dirty.bind(this.mesh_source.viewer));
        this._visible = true;
        this._babylon_world_transform_changed_observable = this.root.onAfterWorldMatrixUpdateObservable.add(() => {
            this.mesh_source.viewer.mark_world_bounds_dirty();
        });
        this.mesh_source.viewer.asset_manager.ui_meshes.add(this);
    }
    update_babylon_mesh_settings() {
        const meshes = this.root.getChildMeshes(false);
        meshes.forEach((mesh) => {
            mesh.layerMask = this._render_layer_mask.bitmap;
            mesh.isVisible = this._visible;
        });
        this.mesh_source.viewer.mark_scene_dirty();
    }
    set_render_layer_mask(mask) {
        if (this._render_layer_mask.bitmap === mask.bitmap)
            return;
        this._render_layer_mask = Object.assign({}, mask);
        this.update_babylon_mesh_settings();
    }
    get_render_layer_mask() {
        return this._render_layer_mask;
    }
    get parent() {
        return this._parent;
    }
    // TODO: Will be changed to Nullable<Interactable> once SceneObject api is ready
    set_parent(gizmo_or_object) {
        if (gizmo_or_object === null) {
            this.root.parent = null;
            this._parent = null;
            // SceneObject
        }
        else if (typeof gizmo_or_object === 'number') {
            const object = this.mesh_source.viewer.viewer_scene.objects.get(gizmo_or_object);
            this.root.parent = object.root;
            this._parent = object;
            // GizmoMeshInstance
        }
        else if ('root' in gizmo_or_object && gizmo_or_object.root instanceof BABYLON_TransformNode) {
            this.root.parent = gizmo_or_object.root;
            this._parent = gizmo_or_object;
        }
        else {
            throw new Error('Invalid parent type for gizmo mesh instance');
        }
    }
    get position() {
        return this._position;
    }
    set position(value) {
        this._position.x = value.x;
        this._position.y = value.y;
        this._position.z = value.z;
    }
    get rotation() {
        return this._rotation;
    }
    set rotation(value) {
        this._rotation.x = value.x;
        this._rotation.y = value.y;
        this._rotation.z = value.z;
    }
    get scale() {
        return this._scale;
    }
    set scale(value) {
        this._scale.x = value.x;
        this._scale.y = value.y;
        this._scale.z = value.z;
    }
    get color_factor() {
        return this._color;
    }
    set color_factor(value) {
        this.color_alpha_factor.x = value.x;
        this.color_alpha_factor.y = value.y;
        this.color_alpha_factor.z = value.z;
        this.mesh_source.viewer.mark_scene_dirty();
    }
    get alpha_factor() {
        return this.color_alpha_factor.w;
    }
    set alpha_factor(value) {
        this.color_alpha_factor.w = value;
        this.mesh_source.viewer.mark_scene_dirty();
    }
    get_world_transform() {
        return this.root.getWorldMatrix().toIMatrix4ToRef(new IMatrix4());
    }
    prepare_for_rendering() {
        const matrix_determinant_sign = Math.sign(determinant3x3(this.root.computeWorldMatrix().m));
        this.instances.forEach((mesh) => {
            if (mesh.metadata) {
                if (mesh.metadata['gizmo_source_meshes']) {
                    babylon_change_source_mesh(mesh, mesh.metadata['gizmo_source_meshes'][matrix_determinant_sign >= 0 ? 'positive' : 'negative']);
                }
            }
        });
    }
    set visible(visible) {
        if (this._visible === visible)
            return;
        this._visible = visible;
        if (this.instances.length === 0)
            return;
        this.instances.forEach((instance) => {
            instance.isVisible = this._visible;
        });
        this.mesh_source.viewer.mark_scene_dirty();
    }
    get visible() {
        return this._visible;
    }
    get disposed_observable() {
        return this._disposed_observable;
    }
    dispose_meshes() {
        if (this.instances.length === 0)
            return;
        this.mesh_source.viewer.mark_scene_dirty();
        this.instances.forEach((instance) => {
            instance.dispose(true, false);
        });
        this.instances.length = 0;
    }
    dispose() {
        this._babylon_world_transform_changed_observable.remove();
        this._disposed_observable.notify(this);
        this.dispose_meshes();
        this.mesh_source.disconnect_instance(this);
        const was_deleted = this.mesh_source.viewer.asset_manager.ui_meshes.delete(this);
        if (!was_deleted && this.mesh_source.viewer.get_config().verbose_warnings) {
            Logger.as(LOGGER_CATEGORY).warn("Couldn't find reference to currently disposing UiMeshInstance in AssetManager");
        }
        this._disposed_observable.dispose();
    }
    get_world_bounds() {
        return new ITransformedBoundingBox(this.mesh_source.local_bounds, this.get_world_transform());
    }
}
export class Gizmo extends UiMeshInstance {
    constructor(mesh) {
        super(mesh);
        this.mesh_source.viewer.asset_manager.gizmos.add(this);
        this._input_handler = this.mesh_source.viewer.create_input_handler(this, [this]);
    }
    get click_observable() {
        return this._input_handler.click_observable;
    }
    get secondary_click_observable() {
        return this._input_handler.secondary_click_observable;
    }
    get drag_begin_observable() {
        return this._input_handler.drag_begin_observable;
    }
    get drag_observable() {
        return this._input_handler.drag_observable;
    }
    get drag_end_observable() {
        return this._input_handler.drag_end_observable;
    }
    get pointer_enter_observable() {
        return this._input_handler.pointer_enter_observable;
    }
    get pointer_leave_observable() {
        return this._input_handler.pointer_leave_observable;
    }
    get drag_interactable() {
        return this._input_handler.drag_begin_observable.has_active_observers ||
            this._input_handler.drag_observable.has_active_observers ||
            this._input_handler.drag_end_observable.has_active_observers;
    }
    get click_interactable() {
        return this._input_handler.click_observable.has_active_observers;
    }
    get hover_interactable() {
        return this._input_handler.pointer_enter_observable.has_active_observers ||
            this._input_handler.pointer_leave_observable.has_active_observers;
    }
    get is_pickable() {
        return this.drag_interactable || this.click_interactable || this.hover_interactable;
    }
    get has_input_handler() {
        return this.drag_interactable || this.click_interactable || this.hover_interactable;
    }
    dispose() {
        this.mesh_source.viewer.interactable_disposed_observable.notify(this);
        const was_deleted = this.mesh_source.viewer.asset_manager.gizmos.delete(this);
        if (!was_deleted && this.mesh_source.viewer.get_config().verbose_warnings) {
            Logger.as(LOGGER_CATEGORY).warn("Couldn't find reference to currently disposing Gizmo in AssetManager");
        }
        this._input_handler.dispose();
        super.dispose();
    }
}
__decorate([
    Deprecated({ new: 'has_input_handler' })
], Gizmo.prototype, "is_pickable", null);
//# sourceMappingURL=UiMesh.js.map